import {
  AfterViewInit,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';

import { UserInfo } from '../../../../shared/apis/UserMgtAPI/index';
import {
  enumUserRole,
  UserMgtService,
} from '../../../../shared/services/user-management.service';

import { AuthService } from 'app/shared/services/auth.service';
import { UserService } from 'app/shared/services/user.service';
import { take } from 'rxjs/operators';
import { User } from 'app/shared/models/user';
import { KeyValuePair } from 'app/shared/models/keyvaluepair';
import { ValidateUniqueCombination } from 'app/validators/async-unique-combination.validator';
import { DefaultDataService } from 'app/shared/services/default-data.service';
import { Utilities } from 'app/shared/helpers/utilities';
import { Organisation } from 'app/shared/models/organisations';
import { OrganisationService } from 'app/shared/services/organisation.service';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { HyperLawService } from 'app/shared/services/hyper-law.service';
import { OnlineReportSettingService } from 'app/shared/services/online-report-setting.service';
import { environment } from 'environments/environment';

@Component({
  selector: 'user-management-user',
  templateUrl: './user-management-user.component.html',
  styleUrls: ['./user-management-user.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class UserManagementUserComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  form: UntypedFormGroup;
  public title = 'Add User';
  public editingSelf = false;
  userList: User[] = [];
  user: User;
  userRoles: any;
  addUser = false;
  organisations: Organisation[] = [];
  IsOrgAdmin: boolean = false;
  IsAdmin: boolean = false;
  currentUser: User = null;
  tabIndex = 0;

  hyperLawModuleActive = false;
  hyperLawModuleActive$: Subscription;

  onNotificationValueChanges: Subscription;
  onRoleValueChanges: Subscription;
  onManagerIdValueChanges: Subscription;
  onOrganisationIdValueChanges: Subscription;
  showUserReferralNotificationsUI: boolean = false;

  // MRD - note to self...
  // The dlgUserInfo.userInfo.userUniqueId is a guid which should match a corresponding user in the tenant database
  // Therefore we can call get on the userService using the userUniqueId to get the equivalent user record in the tenant database

  constructor(
    public dialogRef: MatDialogRef<UserManagementUserComponent>,
    @Inject(MAT_DIALOG_DATA) public data: dlgUserInfo, // UserInfo,
    public userMgtService: UserMgtService,
    public auth: AuthService,
    private userService: UserService,
    private defaultDataService: DefaultDataService,
    private onlineReportSettingService: OnlineReportSettingService,
    private orgService: OrganisationService,
    private hyperLawService: HyperLawService,
    private fb: UntypedFormBuilder
  ) {
    this.editingSelf = false;
    if (
      data.userInfo !== undefined &&
      data.userInfo.userUniqueId !== undefined
    ) {
      this.addUser = false;
      this.title = 'Edit User';
      if (data.userInfo.loginId === this.auth.getClaim('email')) {
        this.editingSelf = true;
      }
    } else {
      this.addUser = true;
      this.title = 'Add User';
    }

    if (data.dlgTitle !== undefined) {
      this.title = data.dlgTitle;
    }

    this.userRoles = this.userMgtService.userRoles;
    if (
      !this.defaultDataService.enableReferralsAndAssessments ||
      !this.defaultDataService.enableMarkerSearch
    ) {
      this.userRoles = this.userRoles.filter(
        (i) => i.role !== enumUserRole.SearchOnly
      );
    }

    // If not in AAB mode, remove multi-org admin user role.
    if (environment.limitAccessToSubjectsByOrg != true) {
      this.userRoles = this.userRoles.filter(
        (i) => i.role !== enumUserRole.MultiOrgAdmin
      );
    }

    // MRD - sort the user roles by name
    this.userRoles.sort((a, b) => a.name.localeCompare(b.name));

    this.hyperLawModuleActive = this.hyperLawService.isModuleActive();
    this.hyperLawModuleActive$ = this.hyperLawService.moduleActive.subscribe(
      (isActive: boolean) => {
        this.hyperLawModuleActive = isActive;
      }
    );

    // Determine if we should show UI to allow users to get referral notifications.
    this.showUserReferralNotificationsUI =
      environment.limitAccessToSubjectsByOrg == true ||
      this.defaultDataService.enableOnlineReporting;
  }

  ngOnInit() {
    this.organisations = [];
    if (environment.limitAccessToSubjectsByOrg === true) {
      let orgList = this.orgService.getCache();
      if (orgList) {
        this.organisations = orgList
          .filter((i) => !i.hideRecord)
          .sort((a, b) => a.name.localeCompare(b.name));
      } else {
        this.orgService.getAllData().subscribe((r) => {
          this.organisations = r
            .filter((i) => !i.hideRecord)
            .sort((a, b) => a.name.localeCompare(b.name));
        });
      }
    }
    this.form = this.createForm();

    // if User has role OrgManager then remove admin roles from list of allowed roles.
    this.IsOrgAdmin = this.auth.hasPriv('OrgManager');
    if (this.IsOrgAdmin) {
      this.userRoles = this.userRoles.filter(
        (i) => i.role != enumUserRole.Admin && i.role !== enumUserRole.SysAdmin
      );
      this.currentUser = this.userService.getCurrentUser();
    }

    // If Limit access not enabled, remove OrgManager role
    if (environment.limitAccessToSubjectsByOrg === false) {
      this.userRoles = this.userRoles.filter(
        (i) => i.role != enumUserRole.OrgManager
      );
    }

    this.IsAdmin = this.auth.hasPriv('Admin,SysAdmin');

    // if not editing self then the dropdown for selection of manager will be displayed - get the users to display
    if (!this.editingSelf) {
      const userUid =
        this.data.userInfo && this.data.userInfo.userUniqueId
          ? this.data.userInfo.userUniqueId
          : '';

      if (userUid) {
        // get the corresponding local user - we need this to retrieve the managerId and any other properties which are not passed as part of userInfo
        this.userService.get(userUid).subscribe((user) => {
          this.user = user ? new User(user) : new User();
          this.getActiveUsers(user.managerId);
          this.setUser(this.user);
        });
      } else {
        this.user = new User();
        if (this.IsOrgAdmin)
          this.user.organisationId = this.currentUser.organisationId;
        this.setUser(this.user);
        this.getActiveUsers(this.userService.getCurrentUserId());
      }
    }

    this.onRoleValueChanges = this.form
      .get('role')
      .valueChanges.subscribe((val) => {
        if (val == 'multiorgadmin') {
          this.form.controls['organisationId'].clearValidators();
          this.form.controls['organisationId'].markAsPristine();
          this.form.controls['organisationId'].setValue(null);
        } else if (this.organisations.length > 0) {
          this.form.controls['organisationId'].setValidators([
            Validators.required,
          ]);
        }
      });

    this.onNotificationValueChanges = this.form
      .get('receiveNewReferralNotifications')
      .valueChanges.subscribe((val) => {
        return val && this.organisations.length
          ? this.form.controls['organisationId'].setValidators([
              Validators.required,
            ])
          : this.form.controls['organisationId'].clearValidators();
      });
  }

  ngAfterViewInit(): void {
    Utilities.patchCheckboxAria();
  }

  ngOnDestroy(): void {
    this.onManagerIdValueChanges?.unsubscribe();
    this.onNotificationValueChanges?.unsubscribe();
    this.onRoleValueChanges?.unsubscribe();
    this.onOrganisationIdValueChanges?.unsubscribe();
  }

  updatingField: boolean = false;

  setUser(user?: User) {
    if (user) {
      this.form.controls['costPerHour'].setValue(user.costPerHour);
      this.form.controls['canViewArchive'].setValue(user.canViewArchive);
      this.form.controls['hasCasePrepAccess'].setValue(user.hasCasePrepAccess);
      this.onOrganisationIdValueChanges = this.form.controls[
        'organisationId'
      ].valueChanges.subscribe((orgId) => {
        this.updateUsersFromOrg(orgId);
      });
      this.form.controls['organisationId'].setValue(user.organisationId);

      this.onManagerIdValueChanges = this.form.controls[
        'managerId'
      ].valueChanges.subscribe((managerId) => {
        if (this.updatingField === true) return;
        console.log(
          'Manager Changing ' + managerId + ' from ' + this.user.managerId
        );

        if (this.user.managerId != managerId) {
          let manager = this.userList.find((u) => u.id == managerId);
          let orgId = this.form.controls['organisationId'].value || -1;
          if (manager) {
            if (
              manager.organisationId &&
              orgId &&
              manager.organisationId != orgId
            ) {
              this.user.organisationId = manager.organisationId;

              this.updatingField = true;
              this.form.controls['organisationId'].patchValue(
                manager.organisationId
              );
              this.updatingField = false;
            }
            this.updateUsersFromOrgInternal(manager.organisationId, false);
          }
        }
      });
      this.form.controls['managerId'].setValue(user.managerId);

      // add async unique validator to email field
      // MRD - discussed with PT and it is felt that adding a unique constraint on the email address may make user setup for end users more complex, so commented this out
      // this.form.controls['emailAddress'].setAsyncValidators(ValidateUnique.createValidator(this.userService, this.user.id, 'email'));
    }
  }

  updateUsersFromOrg(organisationId: number = -1) {
    console.log(this.updatingField);
    if (this.updatingField) return;

    this.updateUsersFromOrgInternal(organisationId);
  }

  updateUsersFromOrgInternal(
    organisationId: number = -1,
    updateManager: boolean = true
  ) {
    console.log(
      'User Org Changing ' +
        organisationId +
        ' from ' +
        this.user.organisationId
    );

    if (organisationId > 0 && environment.limitAccessToSubjectsByOrg === true) {
      this.userService
        .getActiveUsersForOrganisation(organisationId, false)
        .pipe(take(1))
        .subscribe((users) => {
          this.userList = users;
          if (updateManager == true) {
            // force reset for managerId
            let manager = this.userList.find(
              (u) => u.id == this.user.managerId
            );

            if (manager && manager.organisationId != organisationId) {
              if (manager.role != 'multiorgadmin') this.user.managerId = null;
            }

            this.updatingField = true;
            this.form.controls['managerId'].patchValue(this.user.managerId);
            this.updatingField = false;
          }
        });
    } else {
      this.userService
        .getActiveUsers()
        .pipe(take(1))
        .subscribe((users) => {
          this.userList = users;

          //23/06/2023 (YA): Commented this out as it was affecting the form showing who the manager of a user is. Tested it and didnt notice anything broken so assumed it was fine
          // if (updateManager === true) {
          //   // force reset for managerId
          //   this.updatingField = true;
          //   this.form.controls['managerId'].patchValue(-1);
          //   this.updatingField = false;
          //   this.user.managerId = null;
          // }
        });
    }
  }

  createForm(): UntypedFormGroup {
    const group = this.fb.group({
      firstName: [this.data.userInfo.firstName, Validators.required],
      lastName: [this.data.userInfo.lastName, Validators.required],
      emailAddress: [this.data.userInfo.emailAddress, Validators.required],
      telephone: this.data.userInfo.telephone,
      mobile: this.data.userInfo.mobile,
      addressLine1: this.data.userInfo.addressLine1,
      addressLine2: this.data.userInfo.addressLine2,
      addressLine3: this.data.userInfo.addressLine3,
      addressLine4: this.data.userInfo.addressLine4,
      addressLine5: this.data.userInfo.addressLine5,
      postcode: this.data.userInfo.postcode,
      managerId: null,
      costPerHour: null,
      canLogin: this.data.userInfo.canLogin,
      role: this.data.userInfo.role.value || 'user',
      receiveNewReferralNotifications:
        this.data.userInfo.receiveNewReferralNotifications,
      receiveRetentionNotifications:
        this.data.userInfo.receiveRetentionNotifications,
      canViewArchive: this.data.userInfo.canViewArchive,
      hasCasePrepAccess: this.data.userInfo.hasCasePrepAccess,
      organisationId: this.data.userInfo.organisationId,
      userOrganisations: [this.data.userInfo?.userOrganisations],
    });

    const kvp: KeyValuePair[] = [];
    kvp.push({
      key: 'firstName',
      value: 'firstName',
    });
    kvp.push({
      key: 'lastName',
      value: 'lastName',
    });

    // add async unqiue combination validator to description/subject type id
    group.setAsyncValidators(
      ValidateUniqueCombination.createValidator(
        this.userMgtService,
        this.data.userInfo.userUniqueId,
        kvp
      )
    );

    return group;
  }

  getFormMetadata() {
    const data = this.form.value;
    this.data.userInfo.firstName = data.firstName;
    this.data.userInfo.lastName = data.lastName;
    this.data.userInfo.emailAddress = data.emailAddress;
    this.data.userInfo.telephone = data.telephone;
    this.data.userInfo.mobile = data.mobile;
    this.data.userInfo.addressLine1 = data.addressLine1;
    this.data.userInfo.addressLine2 = data.addressLine2;
    this.data.userInfo.addressLine3 = data.addressLine3;
    this.data.userInfo.addressLine4 = data.addressLine4;
    this.data.userInfo.addressLine5 = data.addressLine5;
    this.data.userInfo.postcode = data.postcode;
    this.data.userInfo.canLogin = data.canLogin;
    this.data.userInfo.receiveNewReferralNotifications =
      data.receiveNewReferralNotifications;
    this.data.userInfo.receiveRetentionNotifications =
      data.receiveRetentionNotifications;
    this.data.userInfo.canViewArchive = data.canViewArchive;
    this.data.userInfo.hasCasePrepAccess = data.hasCasePrepAccess;
    this.data.userInfo.organisationId = data.organisationId;
    this.data.userInfo.userOrganisations = data.userOrganisations;

    if (data.role) {
      this.data.userInfo.isAdmin = data.role === 'admin' ? true : false;
      this.data.userInfo.privilegeString = data.role;
    } else {
      // default to user
      this.data.userInfo.privilegeString = 'user';
    }

    if (!this.editingSelf) {
      this.data.userInfo.role = this.userMgtService.userRoles.find(
        (i) => i.value === this.data.userInfo.privilegeString
      );
      if (this.user && data.managerId !== this.user.managerId) {
        this.user.managerId = data.managerId;
      }

      if (this.user) this.user.costPerHour = data.costPerHour;
    }
  }

  // get the active users - we pass in the existing userId, if one is specified, so the user is included in the list even if they are not active
  private getActiveUsers(currentUserId?: number) {
    this.userService
      .getActiveUsers(currentUserId)
      .pipe(take(1))
      .subscribe((users) => {
        this.userList = users;

        // if current user is OrgManager, then restrict Manager list of users to users in same organisation.
        if (this.IsOrgAdmin) {
          this.userList = users.filter(
            (i) => i.organisationId == this.currentUser.organisationId
          );
        } else if (this.user.organisationId > 0) {
          this.userList = users.filter(
            (i) => i.organisationId == this.user.organisationId
          );
        }
      });
  }

  public confirmSubmit(): void {
    // get form data
    this.getFormMetadata();

    // save back to identity server...
    this.userMgtService.save(this.data.userInfo).subscribe(
      (userInfo: UserInfo) => {
        if (!this.editingSelf && userInfo && this.user) {
          // update/add the manager id - we don't pass the user object as the userMgtService.save may have changed the underlying user object
          this.userService.updateUser(
            this.user.managerId,
            this.user.costPerHour,
            userInfo.userUniqueId,
            this.data.userInfo.receiveNewReferralNotifications,
            this.data.userInfo.receiveRetentionNotifications,
            this.data.userInfo.canViewArchive,
            this.data.userInfo.hasCasePrepAccess,
            this.data.userInfo.organisationId
          );
        }
        // refresh user service cache
        this.userService.refresh().subscribe();
      },
      (error: any) => {
        console.log(error);
        // refresh user service cache
        this.userService.refresh().subscribe();
      }
    );

    if (this.editingSelf) {
      // Update claims
      this.auth.updateUserName(
        this.data.userInfo.firstName + ' ' + this.data.userInfo.lastName
      );
    }
  }

  public resetPassword(): void {
    // console.log('resetPassword', this.data);
    this.userMgtService.resetPassword(this.data.userInfo);
  }

  chkCanLogin_changed(e) {
    if (e.checked == false) {
      console.log(
        'Setting receiveNewReferralNotifications to false as Can Login = false'
      );
      this.form.get('receiveNewReferralNotifications').patchValue(false);
    }
    if (
      e.checked == true &&
      this.form.get('receiveNewReferralNotifications').pristine
    ) {
      this.form.get('receiveNewReferralNotifications').patchValue(true);
    }
  }

  get limitAccessToSubjectsByOrg(): boolean {
    return environment.limitAccessToSubjectsByOrg;
  }

  getOrgIsRequired() {
    let rc: boolean =
      this.form.get('receiveNewReferralNotifications').value &&
      this.organisations.length > 0;

    if (
      this.form.get('role').value == 'admin' &&
      this.form.get('role').value == 'multiorgadmin' &&
      environment.limitAccessToSubjectsByOrg === true
    )
      rc = false;

    return rc;
  }

  getReceiveNewReferralNotificationDisabled() {
    let rc: boolean = this.form.get('canLogin').value == false;
    if (environment.limitAccessToSubjectsByOrg == false) rc = true;
    else {
      if (this.form.get('role').value == 'admin') rc = true;
    }
    return rc;
  }
}

// tslint:disable-next-line:max-classes-per-file class-name
export class dlgUserInfo {
  public userInfo: UserInfo;
  public dlgTitle: string;
}
